import { Row } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner as StencilMessageBanner,
  MessageBannerLink,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import { IconInformationFill } from "@amzn/stencil-react-icons";
import IconAlertTriangleFill from "@amzn/stencil-react-icons/icons/icon-alert-triangle-fill";
import IconCheckCircleFill from "@amzn/stencil-react-icons/icons/icon-check-circle-fill";
import { getSupportInfo } from "prettier";
import React from "react";

import { getString } from "../../uistringlabels/uiStringUtils";

export interface MessageBannerProps {
  /**
   * Unique identifier for the banner
   */
  id: string;

  /**
   * Test ID for the banner
   */
  testId: string;

  /**
   * Whether the banner can be dismissed
   */
  isDismissible?: boolean;

  /**
   * The message to display in the banner
   */
  message: string | React.ReactNode;

  /**
   * The type of banner to display
   */
  type?: MessageBannerType;

  /**
   * Custom icon to display in the banner
   */
  icon?: React.ReactElement;

  /**
   * The font size of the message
   */
  fontSize?: string;

  /**
   * The font weight of the message
   */
  fontWeight?: number | "bold" | "light" | "regular" | "medium";

  /**
   * Additional CSS class name
   */
  className?: string;
}

/**
 * A reusable message banner component
 */
export const MessageBanner: React.FC<MessageBannerProps> = ({
  id,
  testId,
  isDismissible = false,
  message,
  type = MessageBannerType.Warning,
  icon = <IconAlertTriangleFill />,
  fontSize = "100",
  fontWeight = "bold",
  className = "",
}) => {
  return (
    <div id={id} className={`pdp-disclaimer-banner ${className}`}>
      <StencilMessageBanner data-testid={testId} isDismissible={isDismissible} type={type} icon={icon}>
        <Text fontSize={fontSize} fontWeight={fontWeight}>
          {message}
        </Text>
      </StencilMessageBanner>
    </div>
  );
};

/**
 * Banner displayed when the user's OS is not compatible with the product
 */
export const NonCompatibleOSBanner: React.FC = () => {
  return (
    <MessageBanner
      id="non-compatible-os-container"
      testId="non-compatible-os-message-banner"
      message={getString("nonCompatibleOS.messageBanner")}
      type={MessageBannerType.Warning}
      icon={<IconAlertTriangleFill />}
    />
  );
};

/**
 * Banner displayed when the user has already ordered the item
 */
export interface HasOrderedItemBannerProps {
  date: string;
}

export const HasOrderedItemBanner: React.FC<HasOrderedItemBannerProps> = ({ date }) => {
  return (
    <MessageBanner
      id="has-ordered-item-message-banner"
      testId="has-ordered-item-message-banner"
      message={getString("hasOrderedItem.messageBanner", { date })}
      type={MessageBannerType.Warning}
      icon={<IconAlertTriangleFill />}
    />
  );
};

/**
 * Banner displayed when a license limit is reached
 */
export interface LicenseLimitBannerProps {
  managerAlias: string;
  productName: string;
  showFAQ: () => void;
}

export const LicenseLimitBanner: React.FC<LicenseLimitBannerProps> = ({ managerAlias, productName, showFAQ }) => {
  const PHONE_TOOL_USER_PREFIX = "https://phonetool.amazon.com/users/";
  const managerLink = `<a href="${PHONE_TOOL_USER_PREFIX}${managerAlias}" target="_blank" rel="noopener">${managerAlias}</a>`;
  const html = getString("licenseLimit.messageBanner", {
    managerName: managerLink,
    productName,
  });

  return (
    <MessageBanner
      id="license-limit-reached-container"
      testId="license-limit-reached-message-banner"
      isDismissible={true}
      message={
        <>
          <div dangerouslySetInnerHTML={{ __html: html }} style={{ display: "inline" }} />{" "}
          <MessageBannerLink dataTestId="license-limit-reached-faq-button" onClick={showFAQ}>
            {getString("licenseLimit.viewFaq")}
          </MessageBannerLink>
        </>
      }
      fontSize="B200"
      fontWeight={400}
      type={MessageBannerType.Warning}
      icon={<IconAlertTriangleFill />}
    />
  );
};

export const ReturnInitiatedMessageBanner: React.FC = () => {
  return (
    <MessageBanner
      id="return-initiated-container"
      testId="return-initiated-message-banner"
      message={getString("licenseReturn.returnInitiatedMessageBanner")}
      fontSize="B200"
      fontWeight={400}
      type={MessageBannerType.Success}
      icon={<IconCheckCircleFill />}
    />
  );
};

export const HasActiveLicenseBanner: React.FC = () => {
  return (
    <MessageBanner
      id="has-active-license-container"
      testId="has-active-license-message-banner"
      message={getString("licenseReturn.hasActiveLicenseMessageBanner")}
      fontSize="B200"
      fontWeight={400}
      type={MessageBannerType.Warning}
      icon={<IconAlertTriangleFill />}
    />
  );
};

export const BuilderHubBanner: React.FC = () => {
  return (
    <MessageBanner
      id="builder-hub-container"
      testId="builder-hub-banner"
      message={
        <Row gridGap={"S100"}>
          <Text>{getString("aiForMe.builderHubBanner.message")}</Text>
          <MessageBannerLink href={getString("aiForMe.builderHubBanner.linkUrl")} target="_blank">
            {getString("aiForMe.builderHubBanner.linkText")}
          </MessageBannerLink>
        </Row>
      }
      fontSize="B200"
      fontWeight={400}
      type={MessageBannerType.Informational}
      icon={<IconInformationFill />}
      isDismissible
    />
  );
};
